import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getCountries, getStates, getCities } from '@/utils/locationData';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Mail, Phone, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Country codes for phone
const COUNTRY_CODES = [
  { code: '+1', country: 'US/Canada' },
  { code: '+44', country: 'UK' },
  { code: '+91', country: 'India' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+61', country: 'Australia' },
  { code: '+81', country: 'Japan' },
  { code: '+86', country: 'China' },
  { code: '+55', country: 'Brazil' },
  { code: '+52', country: 'Mexico' },
  { code: '+65', country: 'Singapore' },
  { code: '+60', country: 'Malaysia' },
  { code: '+971', country: 'UAE' },
  { code: '+966', country: 'Saudi Arabia' },
];

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone_number: '',
    country_code: '+1',
    city: '',
    state: '',
    pin_code: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Location states
  const [countries] = useState(getCountries());
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Update states when country changes
  useEffect(() => {
    if (formData.country) {
      const countryStates = getStates(formData.country);
      setStates(countryStates);
      // Reset state and city if country changes
      if (formData.state && !countryStates.includes(formData.state)) {
        setFormData(prev => ({ ...prev, state: '', city: '' }));
        setCities([]);
      }
    } else {
      setStates([]);
      setCities([]);
    }
  }, [formData.country]);

  // Update cities when state changes
  useEffect(() => {
    if (formData.country && formData.state) {
      const stateCities = getCities(formData.country, formData.state);
      setCities(stateCities);
      // Reset city if state changes
      if (formData.city && !stateCities.includes(formData.city)) {
        setFormData(prev => ({ ...prev, city: '' }));
      }
    } else {
      setCities([]);
    }
  }, [formData.country, formData.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      
      // Prepare login data based on method
      let loginPayload = formData;
      if (isLogin) {
        if (loginMethod === 'email') {
          loginPayload = {
            email: formData.email,
            password: formData.password
          };
        } else {
          loginPayload = {
            phone_number: formData.phone_number,
            country_code: formData.country_code,
            password: formData.password
          };
        }
      }
      
      const response = await axios.post(`${API_URL}${endpoint}`, loginPayload);
      login(response.data.token, response.data.user);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-8 px-4" style={{ background: 'linear-gradient(to bottom right, #4F46E5, #7C3AED, #4F46E5)' }}>
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-5 md:p-8">
          <div className="text-center mb-6">
            <img 
              src="/logo.png" 
              alt="Bid2Barter" 
              className="h-16 md:h-24 w-auto mx-auto mb-3"
            />
            <p className="text-slate-600 text-sm md:text-base">Trade what you have for what you want</p>
          </div>

          <div className="flex space-x-2 mb-5">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 rounded-full font-semibold transition-all ${
                isLogin
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="login-tab"
            >
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 rounded-full font-semibold transition-all ${
                !isLogin
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="register-tab"
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                  <input
                    type="text"
                    required={!isLogin}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all h-12 px-4"
                    placeholder="Your name"
                    data-testid="name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Country</label>
                    <SearchableSelect
                      options={countries}
                      value={formData.country}
                      onChange={(value) => setFormData({ ...formData, country: value, state: '', city: '' })}
                      placeholder="Select Country"
                      testId="country-select"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                    <SearchableSelect
                      options={states}
                      value={formData.state}
                      onChange={(value) => setFormData({ ...formData, state: value, city: '' })}
                      placeholder="Select State"
                      disabled={!formData.country}
                      testId="state-select"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">City</label>
                    <SearchableSelect
                      options={cities}
                      value={formData.city}
                      onChange={(value) => setFormData({ ...formData, city: value })}
                      placeholder="Select City"
                      disabled={!formData.state}
                      testId="city-select"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Pin Code</label>
                    <input
                      type="text"
                      value={formData.pin_code}
                      onChange={(e) => setFormData({ ...formData, pin_code: e.target.value })}
                      className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all h-12 px-4"
                      placeholder="Pin code"
                      data-testid="pincode-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number (Optional - for password recovery)</label>
                  <div className="flex gap-2">
                    <select
                      value={formData.country_code}
                      onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                      className="w-28 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all h-12 px-2 text-sm"
                      data-testid="phone-country-code"
                    >
                      {COUNTRY_CODES.map((cc) => (
                        <option key={cc.code} value={cc.code}>
                          {cc.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value.replace(/\D/g, '') })}
                      className="flex-1 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all h-12 px-4"
                      placeholder="Phone number"
                      data-testid="phone-number-input"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Login Method Toggle - Only show for login */}
            {isLogin && (
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setLoginMethod('email')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
                    loginMethod === 'email'
                      ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500'
                      : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
                  }`}
                  data-testid="login-email-tab"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setLoginMethod('phone')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
                    loginMethod === 'phone'
                      ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500'
                      : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
                  }`}
                  data-testid="login-phone-tab"
                >
                  <Phone className="w-4 h-4" />
                  Phone
                </button>
              </div>
            )}

            {/* Email input - show for register always, or for login when email method selected */}
            {(!isLogin || loginMethod === 'email') && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  required={!isLogin || loginMethod === 'email'}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all h-12 px-4"
                  placeholder="your@email.com"
                  data-testid="email-input"
                />
              </div>
            )}

            {/* Phone input for login */}
            {isLogin && loginMethod === 'phone' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                <div className="flex gap-2">
                  <select
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                    className="w-24 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all h-12 px-2 text-sm"
                    data-testid="login-country-code"
                  >
                    {COUNTRY_CODES.map((cc) => (
                      <option key={cc.code} value={cc.code}>
                        {cc.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    required
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value.replace(/\D/g, '') })}
                    className="flex-1 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all h-12 px-4"
                    placeholder="Enter your phone number"
                    data-testid="login-phone-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all h-12 px-4 pr-12"
                  placeholder="••••••••"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-full font-bold px-8 py-3 bg-indigo-600 text-white hover:bg-indigo-700 transition-all active:scale-95 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 disabled:opacity-50"
              data-testid="auth-submit-button"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>

            {isLogin && (
              <div className="text-center mt-4">
                <a
                  href="/forgot-password"
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </a>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
